import { createRoute, redirect } from '@tanstack/react-router'
import { lazy } from 'react'
import type { LazyPage } from './lazy-page'
import { guardAt } from './products'
import { rootRoute } from './root'

type BillingTab = import('../../routes/BillingPage').BillingTab
const BillingPage: LazyPage<{ tab: BillingTab }> = lazy(() =>
  import('../../routes/BillingPage').then((m) => ({ default: m.BillingPage })),
)
const CampaignPage: LazyPage<{
  campaignId: string
  view: 'gifts' | 'dedications' | 'pages'
  year?: string
  htmlPage?: string
}> = lazy(() =>
  import('../../routes/CampaignPage').then((m) => ({
    default: m.CampaignPage,
  })),
)
// Billing's pages, listed under it in the sidebar: /billing/overview ·
// /billing/ledger · /billing/memberships · /billing/schedules ·
// /billing/campaigns · /billing/items · /billing/chart-of-accounts. The
// Ledger's other lists are /billing/ledger/donations and /pledges, and
// Schedules' other view is /billing/schedules/rules — top-bar tabs on their
// page. The section's own address opens the overview.
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: () => {
    throw redirect({ to: '/billing/$tab', params: { tab: 'overview' } })
  },
})
const billingLedgerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/ledger',
  component: () => <BillingPage tab="outstanding" />,
})
function BillingLedgerView() {
  const { view } = billingLedgerViewRoute.useParams()
  // An unknown list lands on the Ledger's own rather than a dead screen.
  return (
    <BillingPage
      tab={view === 'donations' || view === 'pledges' ? view : 'outstanding'}
    />
  )
}
const billingLedgerViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/ledger/$view',
  component: BillingLedgerView,
})
const campaignViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/campaigns/$id/$view',
  validateSearch: (raw: Record<string, unknown>) => ({
    ...(typeof raw.year === 'string' && raw.year ? { year: raw.year } : {}),
    ...(typeof raw.page === 'string' && raw.page ? { page: raw.page } : {}),
  }),
  component: function CampaignViewRoute() {
    const { id, view } = campaignViewRoute.useParams()
    const { year, page: htmlPage } = campaignViewRoute.useSearch()
    return (
      <CampaignPage
        campaignId={id}
        view={view === 'dedications' || view === 'pages' ? view : 'gifts'}
        year={year}
        htmlPage={htmlPage}
      />
    )
  },
})
function BillingSchedulesView() {
  const { view } = billingSchedulesViewRoute.useParams()
  // An unknown view lands on the timeline rather than a dead screen.
  return <BillingPage tab={view === 'rules' ? 'rules' : 'schedules'} />
}
const billingSchedulesViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/schedules/$view',
  component: BillingSchedulesView,
})
/**
 * The addresses the views had before the section was regrouped, kept as
 * redirects: a bookmark or a link in an old message still lands on the list
 * it meant.
 */
const BILLING_MOVED: Record<string, BillingTab> = {
  donations: 'donations',
  /* Dues was the list of what is raised on everyone — the rules, not the
     timeline that now leads Schedules. */
  dues: 'rules',
}
function BillingTabView() {
  const { tab } = billingTabRoute.useParams()
  return (
    <BillingPage
      tab={
        tab === 'chart-of-accounts'
          ? 'chart'
          : tab === 'overview' ||
              tab === 'memberships' ||
              tab === 'campaigns' ||
              tab === 'items' ||
              tab === 'schedules'
            ? tab
            : 'outstanding'
      }
    />
  )
}
const billingTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/$tab',
  beforeLoad: ({ params }) => {
    const moved = BILLING_MOVED[params.tab]
    if (moved === 'donations') {
      throw redirect({
        to: '/billing/ledger/$view',
        params: { view: 'donations' },
      })
    }
    if (moved === 'rules') {
      throw redirect({
        to: '/billing/schedules/$view',
        params: { view: 'rules' },
      })
    }
    if (moved) throw redirect({ to: '/billing/$tab', params: { tab: moved } })
    // An unknown tab lands on the Ledger, at the Ledger's own address,
    // rather than drawing it under a name no page is marked for.
    if (
      ![
        'overview',
        'memberships',
        'campaigns',
        'items',
        'schedules',
        'chart-of-accounts',
      ].includes(params.tab)
    ) {
      throw redirect({ to: '/billing/ledger' })
    }
    // A membership's tiers are a shul's.
    if (params.tab === 'memberships') guardAt('/billing/memberships')()
  },
  component: BillingTabView,
})

export const billingRoutes = [
  billingRoute,
  billingLedgerRoute,
  billingLedgerViewRoute,
  campaignViewRoute,
  billingSchedulesViewRoute,
  billingTabRoute,
]
